import { getVariantPath } from '@uswitch/ev-utils/get-variant-path'
import { useEntities, useEntity, useMeta } from '@uswitch/ev-utils/context/selectors'
import { contentTypeId } from '@uswitch/ev-utils/contentful'
import { Type } from '@uswitch/spark'
import { List as SparkList, ListItem as SparkListItem } from '@uswitch/spark.list'
import { DynamicSparkIcon } from '../../elements/spark-icons/dynamic-spark-icon'
import { Link } from '@uswitch/ev-core/react'

const stylePath = (field, variant) =>
  getVariantPath({
    atomicType: 'modules',
    componentId: 'subNavigation',
    variantName: variant,
    field,
  })

const SubNavigationBlocks = ({ lists = [] }) => {
  const linkListEntities = useEntities(lists).map(({ links, subtitle }) => ({
    links,
    subtitle,
  }))

  const listLength = linkListEntities.length

  return (
    <div
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: ['column', 'row'],
        flexWrap: 'wrap',
        justifyContent: 'space-between',
      }}
    >
      <For each='listEntity' index='idx' of={linkListEntities}>
        <SubNavigationList listEntity={listEntity} key={idx} last={idx + 1 === listLength} />
      </For>
    </div>
  )
}

const SubNavigationList = ({ listEntity: { links, subtitle }, last }) => {
  const linkEntities = useEntities(links)
  const parentListIncludesIcon = linkEntities.some(({ icon }) => icon)

  return (
    <div
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexBasis: '100%',
        marginBottom: last ? 0 : ['0', 'xl'],
      }}
    >
      <If condition={subtitle}>
        <Type.H5 sx={{ marginBottom: ['md', 'md', 'lg'] }}>{subtitle}</Type.H5>
      </If>
      <SparkList
        sx={{
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr 1fr', '1fr 1fr 1fr'],
          gridGap: '16px',
        }}
      >
        <For each='link' index='idx' of={linkEntities}>
          <SubNavigationLink
            link={link}
            parentListIncludesIcon={parentListIncludesIcon}
            key={idx}
          />
        </For>
      </SparkList>
    </div>
  )
}

// Links
const SubNavigationLink = ({ link, parentListIncludesIcon }) => {
  const contentType = contentTypeId(link)
  const { icon } = link
  const isUrl = contentType === 'url'

  return (
    <div
      sx={{
        backgroundColor: 'grey-0',
      }}
    >
      <Choose>
        <When condition={isUrl}>
          <UrlLinkCard link={link} icon={icon} parentListIncludesIcon={parentListIncludesIcon} />
        </When>
        <Otherwise>
          <LinkListLinkCard
            link={link}
            icon={icon}
            parentListIncludesIcon={parentListIncludesIcon}
          />
        </Otherwise>
      </Choose>
    </div>
  )
}

const UrlLinkCard = ({ link, icon, parentListIncludesIcon }) => {
  const { linkText, variants: pages, url } = link
  const baseUrl = useMeta(['authorSchema', 'organizationUrl']).slice(0, -1)

  const pageEntities = useEntities(pages)
  const filteredPages = pageEntities.filter(variant => {
    return contentTypeId(variant) === 'richPage' || contentTypeId(variant) === 'modularPage'
  })

  const linkHref = baseUrl + url
  const title = linkText || filteredPages[0].title

  return (
    <LinkItem
      linkHref={linkHref}
      title={title}
      icon={icon}
      parentListIncludesIcon={parentListIncludesIcon}
    />
  )
}

const LinkListLinkCard = ({ link, icon, parentListIncludesIcon }) => {
  const { title, url, urlRef } = link
  const baseUrl = useMeta(['authorSchema', 'organizationUrl']).slice(0, -1)
  const urlRefEntity = useEntity(urlRef)

  const linkHref = urlRefEntity?.url ? baseUrl + urlRefEntity.url : url

  return (
    <LinkItem
      linkHref={linkHref}
      title={title}
      icon={icon}
      parentListIncludesIcon={parentListIncludesIcon}
    />
  )
}

const LinkItem = ({ linkHref, title, icon, parentListIncludesIcon }) => {
  return (
    <Link
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px',
        textDecoration: 'none',
        borderBottom: 'none',
        svg: {
          fill: 'currentColor',
        },
        marginBottom: 0,
      }}
      href={linkHref}
    >
      <SparkListItem
        icon={
          icon && (
            <DynamicSparkIcon
              glyph={icon}
              sx={{
                width: '20px',
                strokeWidth: 0.75,
                marginLeft: '10px',
                color: 'grey-100',
                variant: stylePath('linkCard.icon', 'blocks'),
              }}
            />
          )
        }
        sx={{
          marginLeft: !icon && parentListIncludesIcon ? '50px' : icon ? '0' : '10px',
          padding: '0 20% 0 0',
          textDecoration: 'none',
          alignItems: 'center',
          flex: '1',
        }}
        as='div'
      >
        <Type.Bold1
          as='span'
          sx={{ wordBreak: 'break-word', variant: stylePath('linkCard.text', 'blocks') }}
        >
          {title}
        </Type.Bold1>
      </SparkListItem>
      <div
        sx={{
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'flex-start',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'grey',
          boxSizing: 'border-box',
          padding: ['12px', '16px'],
          maxHeight: '50px',
          variant: stylePath('buttonContainer', 'blocks'),
        }}
      >
        <DynamicSparkIcon
          glyph={'arrow'}
          direction='right'
          sx={{
            width: '13.3px',
            height: '13.3px',
            color: 'grey-100',
            variant: stylePath('linkCard.arrow', 'blocks'),
          }}
        />
      </div>
    </Link>
  )
}

export default SubNavigationBlocks
